import { ICartItem } from '@/api/types/cart';
import AnniCountDown from '@/components/AnniCountDown/AnniCountDown';
import Button from '@/components/Button';
import ProductImageWithLenses from '@/components/Product/ProductImageWithLenses';
import ZeeloolCheckBox from '@/components/ZeeloolCheckBox';
import { IS_DE } from '@/utils/constants';
import { EPrescriptionType } from '@/utils/enums/goods';
import {
  CloseOutlined,
  CollapseArrowDownOutlined,
  DeleteOutlined,
  InfoOutlined,
  MinusOutlined,
  PlusOutlined,
  QuestionOutlined,
} from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import React, { useCallback } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import BlueLightItem from '../cartItemComponents/BlueLightItem';
import GiftsWithCartItem from '../cartItemComponents/GiftsWithCartItem';
import { ProductImageTags } from '../cartItemComponents/ProductImageTags';

interface CartItemMobileProps {
  cartItemData: ICartItem;
  batchFlag: boolean;
  selected?: boolean;
  itemDisabled?: boolean; // 商品是否禁用
  itemPromotion?: any;
  itemType?: number;
  giftCountDown?: number;
  giftAllowType?: number;

  // 从hooks传来的状态和方法
  loading: boolean;
  showQtyOperationBtn: boolean;
  setShowQtyOperationBtn: (show: boolean) => void;
  swiperRef: React.RefObject<SwiperType | null>;
  showPreCountDown: boolean;
  afterAnniGreaterFlag: boolean;
  showChangeColor: boolean;
  showCartDetailPopup: boolean;
  outStock: boolean;
  lensCanotUse: boolean;
  editShow: boolean;
  flag1: boolean;
  flag2: boolean;
  isGlassesGrayImage: boolean;

  // 方法
  formatPrice: (price: number | string) => string;
  getProductImage: () => string;
  getProductName: () => string;
  getItemTotalPrice: () => number;
  showDownPrice: () => boolean;
  getDownPrice: () => number;
  qtyChange: (item: any, type: 'reduce' | 'add') => Promise<void>;
  handleDelete: () => void;
  deleteItem: (cartItem: ICartItem) => void;
  handleMoveToWishlist: () => void;
  handleEditPrescription: () => void;
  handleAddLens: () => void;
  handleRemoveLens: () => Promise<void>;
  handleSelectChange: (checked: boolean) => void;
  closeSwiper: () => void;
  handleSliderMove: () => void;
  getPromotionItemText: (qty: number) => string;
  shouldShowOverStockWarning: () => boolean;
  canChangeColorClick: () => boolean;
  handleChangeColorClick: (event?: React.MouseEvent<HTMLElement>) => void;
  handlePrescriptionTypeClick: (event?: React.SyntheticEvent, expanded?: boolean) => void;
  handleOpenLimitBuyDialog: () => void;
  handleCountDownEnd: () => void;
  handleChangeAfterAnniGreaterFlag: (flag: boolean) => void;
  getIntroText: () => string;
  handleProductImageClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;

  // 兑换相关方法和数据
  exchangeCouponList: any[];
  canUseRedeemCouponToRedeem: () => boolean;
  showRedemptionDialog: () => void;
  showCancelRedemptionDialog: () => void;

  // hooks提供的方法
  onReloadCart: (data?: any) => void;

  // Swiper管理器
  swiperManager?: any;
}

export const CartItemMobile: React.FC<CartItemMobileProps> = ({
  cartItemData,
  batchFlag,
  selected = false,
  itemDisabled = false,
  itemPromotion,
  itemType = 1,
  giftCountDown = 0,
  giftAllowType = 0,
  swiperManager,

  // 从hooks传来的状态和方法
  loading,
  showQtyOperationBtn,
  setShowQtyOperationBtn,
  swiperRef,
  showPreCountDown,
  afterAnniGreaterFlag,
  showChangeColor,
  showCartDetailPopup,
  outStock,
  lensCanotUse,
  editShow,
  flag1,
  flag2,
  isGlassesGrayImage,

  // 方法
  formatPrice,
  getProductImage,
  getProductName,
  getItemTotalPrice,
  showDownPrice,
  getDownPrice,
  qtyChange,
  handleDelete,
  deleteItem,
  handleMoveToWishlist,
  handleEditPrescription,
  handleAddLens,
  handleRemoveLens,
  handleSelectChange,
  closeSwiper,
  handleSliderMove,
  getPromotionItemText,
  shouldShowOverStockWarning,
  canChangeColorClick,
  handleChangeColorClick,
  handlePrescriptionTypeClick,
  handleOpenLimitBuyDialog,
  handleCountDownEnd,
  handleChangeAfterAnniGreaterFlag,
  getIntroText,
  handleProductImageClick,
  // 兑换相关方法和数据
  exchangeCouponList,
  canUseRedeemCouponToRedeem,
  showRedemptionDialog,
  showCancelRedemptionDialog,
  onReloadCart,
}) => {
  const t = useTranslations('cart.cartItem');
  // 注册当前Swiper到管理器
  React.useEffect(() => {
    if (swiperManager && cartItemData.cart_id) {
      swiperManager.registerSwiper(cartItemData.cart_id, swiperRef);

      return () => {
        swiperManager.unregisterSwiper(cartItemData.cart_id);
      };
    }
  }, [swiperManager, cartItemData.cart_id, swiperRef]);

  // 包装原有的handleSliderMove，添加关闭其他swiper的逻辑
  const wrappedHandleSliderMove = React.useCallback(() => {
    // 调用原有的逻辑
    handleSliderMove();

    // 关闭其他打开的swiper
    if (swiperManager && cartItemData.cart_id) {
      swiperManager.closeOthersSwiper(cartItemData.cart_id);
    }
  }, [handleSliderMove, swiperManager, cartItemData.cart_id]);

  // 监听内容变化，手动触发Swiper高度更新
  React.useEffect(() => {
    if (swiperRef.current) {
      // 使用requestAnimationFrame确保DOM更新完成
      requestAnimationFrame(() => {
        swiperRef.current?.updateAutoHeight(300);
        swiperRef.current?.update();
      });
    }
  }, [
    // 监听所有可能影响内容高度的状态变化
    (cartItemData.lens as any)?.new_lens,
    (cartItemData.option as any)?.old_lens_id,
    selected,
    showQtyOperationBtn,
    lensCanotUse,
    // 兑换品展示相关状态
    exchangeCouponList?.length,
    (exchangeCouponList?.[0] as any)?.number, // 监听兑换券数量变化
    (cartItemData?.product as any)?.is_exchange,
    itemType,
    outStock,
    batchFlag, // 编辑模式会影响兑换按钮的可用性
    // 计价相关状态变化
    itemPromotion?.product_payable, // 促销应付价格
    itemPromotion?.product_rule_discounts_price, // 促销规则折扣价格
    itemPromotion?.product_price, // 促销商品价格
    itemPromotion?.product_original_price, // 促销原价
    cartItemData.product?.price_info, // 商品价格信息变化
    cartItemData.lens?.lens_price, // 镜片价格变化
    cartItemData.lens?.lens_type_price, // 镜片类型价格变化
    cartItemData.lens?.index_price, // 折射率价格变化
    cartItemData.coating?.price_info, // 膜层价格变化
  ]);

  // 修复no-param-reassign: 使用useCallback优化swiperRef赋值
  const handleSwiperInit = useCallback(
    (swiper: SwiperType) => {
      if (swiperRef.current !== swiper) {
        swiperRef.current = swiper;
      }
    },
    [swiperRef],
  );

  // 渲染数量控制组件，避免嵌套三元表达式
  const renderQuantityControl = () => {
    if (itemType === 4) {
      // 赠品数量
      return (
        <div className="flex items-center">
          {batchFlag ? (
            <Button
              className="w-[25px] h-[25px] flex items-center justify-center !bg-[#f8e8e8] !rounded-[6px] !p-0 !text-[13px] !text-[#bc1b1b] !min-w-[25px]"
              onClick={() => deleteItem(cartItemData)}
              sx={{
                border: 'none',
                padding: '0px',
              }}
            >
              <DeleteOutlined className="!text-14" />
            </Button>
          ) : (
            <div className="h-[25px] w-[25px] text-center leading-[25px] text-[12px]">
              <span>x</span>1
            </div>
          )}
        </div>
      );
    }

    if (itemType !== 1) {
      // 兑换品和其他非普通商品，不管是否编辑模式，都只展示数量
      return (
        <div className="h-[25px] w-[25px] text-center leading-[25px] text-[12px]">
          <span>x</span>1
        </div>
      );
    }

    // 普通商品的数量控制
    return <div className="flex items-center">{renderQuantityDisplay()}</div>;
  };

  // 渲染促销相关提示，避免嵌套三元表达式
  const renderPromotionTips = () => {
    // 基本条件检查
    if (lensCanotUse || itemType !== 1 || cartItemData.qty > cartItemData.stock) {
      return null;
    }

    // 检查是否有促销信息
    if (!itemPromotion || itemPromotion.products_promotions_show !== 1) {
      // 没有促销信息时，检查价格优惠提示
      if (showDownPrice()) {
        return (
          <div className="mt-[7px] text-[12px] text-[#b06814]">
            {formatPrice(getDownPrice())} {t('cheaperThanAdded')}
          </div>
        );
      }
      return null;
    }

    // 有促销信息的情况
    if (itemPromotion.over_limit_rule === 1) {
      // 第一优先级：有折扣商品的促销提示
      if (
        itemPromotion.products_promotions_qty > 0 &&
        cartItemData.qty !== itemPromotion.products_promotions_qty
      ) {
        return (
          <div
            className="mt-[5px] text-[12px] text-[#b06814] flex items-center cursor-pointer"
            onClick={handleOpenLimitBuyDialog}
          >
            <QuestionOutlined className="mr-[2px] text-[12px]" />
            {t('include')} {itemPromotion.products_promotions_qty} {t('discounted')}{' '}
            {getPromotionItemText(itemPromotion.products_promotions_qty)}.
          </div>
        );
      }

      // 第二优先级：折扣已用完提示
      if (itemPromotion.products_promotions_qty <= 0) {
        return (
          <div
            className="mt-[5px] text-[12px] text-[#b06814] flex items-center cursor-pointer"
            onClick={handleOpenLimitBuyDialog}
          >
            <QuestionOutlined className="mr-[2px] text-[12px]" />
            {t('discountUsedUp')}
          </div>
        );
      }
    }

    // 第三优先级：价格优惠提示
    if (showDownPrice()) {
      return (
        <div className="mt-[7px] text-[12px] text-[#b06814]">
          {formatPrice(getDownPrice())} {t('cheaperThanAdded')}
        </div>
      );
    }

    return null;
  };

  // 获取产品图片的className
  const getProductImageClassName = () => {
    let widthClass = '!w-full';
    if (cartItemData.product.is_glass === 1) {
      widthClass = '!w-[82px]'; // 移动端82px
    }
    const blendClass = isGlassesGrayImage ? 'mix-blend-normal' : 'mix-blend-multiply';
    return `${widthClass} object-cover ${blendClass}`;
  };

  // 渲染数量显示
  const renderQuantityDisplay = () => {
    // 编辑模式下显示删除按钮
    if (batchFlag) {
      return (
        <Button
          className="w-[25px] h-[25px] flex items-center justify-center !bg-[#f8e8e8] !rounded-[6px] !p-0 !text-[13px] !text-[#bc1b1b] !min-w-[25px]"
          onClick={() => deleteItem(cartItemData)}
          sx={{
            border: 'none',
            padding: '0px',
          }}
        >
          <DeleteOutlined className="!text-14" />
        </Button>
      );
    }

    // 获取当前数量和相关数据
    const currentQty = selected && itemPromotion ? itemPromotion.qty : cartItemData.qty;
    const currentItem = selected && itemPromotion ? itemPromotion : cartItemData;
    const isOverStock = currentQty > cartItemData.stock;

    // 数量按钮的通用样式
    const quantityButtonSx = {
      border: 'none',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&.Mui-disabled': {
        border: 'none',
        backgroundColor: 'transparent',
        opacity: 0.4,
      },
    };

    // 渲染折叠状态的数量显示
    const renderFoldedQuantity = () => (
      <div
        className="flex items-center justify-center h-[25px] w-[25px] bg-[#f6f6f6] rounded-[6px] text-primary-black text-[12px] cursor-pointer"
        onClick={() => !batchFlag && currentQty > 1 && setShowQtyOperationBtn(true)}
      >
        <span className="font-br">x</span>
        {currentQty}
      </div>
    );

    // 渲染展开状态的数量控制
    const renderUnfoldedQuantity = () => (
      <div className="flex items-center w-[75px] h-[25px] leading-[25px] text-center">
        <Button
          disabled={loading || currentQty <= 1 || batchFlag}
          onClick={() => qtyChange(currentItem, 'reduce')}
          className="!w-[25px] !h-[25px] !leading-[25px] !p-0 !min-w-0"
          sx={quantityButtonSx}
        >
          <MinusOutlined className="text-[16px] !text-primary-black" />
        </Button>
        <span
          className={`flex-1 text-[12px] bg-[#f6f6f6] rounded-[6px] text-center ${isOverStock ? 'text-[#bc1b1b]' : ''}`}
        >
          {currentQty}
        </span>
        <Button
          disabled={loading || currentQty >= 5 || currentQty >= cartItemData.stock || batchFlag}
          onClick={() => qtyChange(currentItem, 'add')}
          className="!w-[25px] !h-[25px] !leading-[25px] !p-0 !min-w-0"
          sx={quantityButtonSx}
        >
          <PlusOutlined className="text-[16px] !text-primary-black" />
        </Button>
      </div>
    );

    return showQtyOperationBtn ? renderUnfoldedQuantity() : renderFoldedQuantity();
  };

  // 渲染超库存提示
  const renderOverStockWarning = () => {
    if (!shouldShowOverStockWarning()) return null;

    return (
      <div className="mt-[7px] text-[12px] text-[#bc1b1b]">
        {t('lowInventoryModify', { stock: cartItemData.stock })}
      </div>
    );
  };

  return (
    <div className={`font-br bg-white rounded-[10px] overflow-hidden`}>
      <Swiper
        onSwiper={handleSwiperInit}
        freeMode={false}
        slidesPerView="auto"
        resistanceRatio={0}
        autoHeight
        observer
        observeParents
        observeSlideChildren
        onSliderMove={wrappedHandleSliderMove}
        allowTouchMove={!batchFlag}
        className="w-full overflow-hidden"
      >
        {/* 主内容区域 */}
        <SwiperSlide style={{ width: '100%' }}>
          {/* 可兑换横条 - 仿照Vue项目的redeemed样式 */}
          {canUseRedeemCouponToRedeem() && (
            <div
              className={`flex justify-between items-center px-[12px] py-[7px] pb-[12px] bg-[#ebebff] ${batchFlag ? 'opacity-50' : ''}`}
            >
              <div className="text-[12px]">
                {exchangeCouponList &&
                exchangeCouponList.length > 0 &&
                (exchangeCouponList[0] as any)?.number > 1
                  ? t('currentCoupons')
                  : t('currentCoupon')}{' '}
                {t('available')}{' '}
                {exchangeCouponList && exchangeCouponList.length > 0
                  ? (exchangeCouponList[0] as any)?.number || 1
                  : 1}
              </div>
              <div
                className={`text-[12px] px-[8px] rounded-[50px] border border-[#a9a9a9] ${batchFlag ? 'cursor-not-allowed' : ''}`}
                onClick={() => !batchFlag && showRedemptionDialog()}
              >
                {t('redeemedItem')}
              </div>
            </div>
          )}

          <div
            className={`relative w-full box-border pt-[15px] pb-[12px] pl-[12px] bg-[#ffffff] ${canUseRedeemCouponToRedeem() ? 'rounded-t-[10px] translate-y-[-7px]' : ''}`}
          >
            <div className="flex">
              <div className="flex items-center mr-[12px] h-[fit-content]">
                {/* 复选框 */}
                <div className="pr-[8px]">
                  <ZeeloolCheckBox
                    checked={selected}
                    disabled={itemDisabled}
                    onChange={(e) => handleSelectChange(e.target.checked)}
                    fontSize={16}
                  />
                </div>
                {/* 商品图片 */}
                <Link
                  href={`/goods-detail/${cartItemData.product?.shop_sku}`}
                  className={`block relative w-[98px] h-[98px] box-border border border-[#dce1e5] rounded-[8px] z-[1] bg-[#eeeeee] flex items-center justify-center overflow-hidden ${batchFlag ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
                  onClick={handleProductImageClick}
                >
                  <ProductImageWithLenses
                    imgUrl={getProductImage()}
                    product={cartItemData.product || {}}
                    lens={cartItemData.lens || {}}
                    className={getProductImageClassName()}
                  />

                  {/* 产品图片标签提示等 */}
                  <ProductImageTags
                    item={cartItemData}
                    itemType={itemType}
                    selected={selected}
                    itemPromotion={itemPromotion}
                    outStock={outStock}
                    flag1={flag1}
                    flag2={flag2}
                  />

                  {/* 已兑换商品覆盖标签 */}
                  {itemType === 5 && (
                    <div className="absolute bottom-0 left-0 w-full h-[18px] bg-[#ecebfd] text-[10px] flex items-center justify-center rounded-b-[8px] z-10">
                      {t('redeemedItem')}
                    </div>
                  )}
                </Link>
              </div>

              {/* 商品信息 */}
              <div className={`relative flex-1 box-border flex flex-col pr-[10px] justify-between`}>
                <div className={`text-secondary-black`}>
                  {/* 商品名称 */}
                  <div
                    className={`mb-[4px] text-primary-black text-[14px] font-bs pt-[1px] ${itemDisabled && itemType === 4 ? 'opacity-50' : ''}`}
                  >
                    {getProductName()}
                  </div>

                  {/* 颜色 */}
                  {canChangeColorClick() ? (
                    <div className={`w-full ${itemDisabled && itemType === 4 ? 'opacity-50' : ''}`}>
                      <Button
                        disabled={batchFlag}
                        className="flex items-center !px-[7px] !py-[0] !h-[23px] !rounded-[6px] !text-primary-black !text-[12px] w-fit !mb-[7px]"
                        onClick={() => handleChangeColorClick()}
                        sx={{
                          border: 'none',
                          backgroundColor: '#F6F6F6',
                          '&.Mui-disabled': {
                            opacity: 0.5,
                          },
                        }}
                      >
                        {t('color')}: <span className="ml-[3px]">{cartItemData.product.color}</span>
                        <CollapseArrowDownOutlined
                          className={`ml-[2px] text-[15px] text-[#a9a9a9] transition-transform ${showChangeColor ? 'rotate-180' : ''}`}
                        />
                      </Button>
                    </div>
                  ) : (
                    <div
                      className={`text-[12px] text-primary-black mb-[7px] flex items-center ${itemDisabled && itemType === 4 ? 'opacity-50' : ''}`}
                    >
                      {t('color')}: <span className="ml-[3px]">{cartItemData.product.color}</span>
                    </div>
                  )}

                  {/* 添加镜片 */}
                  {!lensCanotUse &&
                    cartItemData.option.prescription_type === EPrescriptionType.FRAME_ONLY &&
                    cartItemData.product.product_lens === 1 &&
                    cartItemData.product.is_glass === 1 &&
                    cartItemData.product.is_show_add_lense === 1 && (
                      <Button
                        disabled={batchFlag}
                        className="flex items-center px-[7px] py-[4px] bg-[#ecebfd] !text-primary-black rounded-[6px] !text-[12px]"
                        onClick={handleAddLens}
                        sx={{
                          border: 'none',
                          backgroundColor: '#ecebfd',
                          borderRadius: '6px',
                          height: '23px',
                          '&.Mui-disabled': {
                            backgroundColor: '#ecebfd',
                            opacity: 0.5,
                          },
                        }}
                      >
                        {t('selectLenses')}
                        <PlusOutlined className="ml-[2px] text-[12px]" />
                      </Button>
                    )}

                  {/* 处方类型 */}
                  {!lensCanotUse &&
                    cartItemData.product.is_glass === 1 &&
                    cartItemData.option.prescription_type &&
                    cartItemData.option.prescription_type !== EPrescriptionType.FRAME_ONLY && (
                      <div className={`w-full`}>
                        <Button
                          disabled={batchFlag}
                          className="flex items-center !px-[7px] !py-[0] !h-[23px] !rounded-[6px] !text-primary-black !text-[12px] w-fit"
                          onClick={handlePrescriptionTypeClick}
                          sx={{
                            border: 'none',
                            backgroundColor: '#F6F6F6',
                            '&.Mui-disabled': {
                              opacity: 0.5,
                            },
                          }}
                        >
                          <span className="max-w-[150px] truncate">
                            {cartItemData.option.prescription_type_name}
                          </span>
                          {!lensCanotUse && (
                            <CollapseArrowDownOutlined
                              className={`ml-[2px] text-[15px] text-[#a9a9a9] transition-transform ${showCartDetailPopup ? 'rotate-180' : ''}`}
                            />
                          )}
                        </Button>
                      </div>
                    )}

                  {/* 镜片不匹配 */}
                  {lensCanotUse && (
                    <div>
                      {cartItemData.lens?.is_match === 2 && (
                        <div>
                          <Button
                            disabled={batchFlag}
                            onClick={handleRemoveLens}
                            className={`flex items-center !text-[12px] !px-[7px] !h-[23px] !text-primary-black !w-fit !rounded-[6px]`}
                            sx={{
                              border: 'none',
                              backgroundColor: '#F6F6F6',
                              '&.Mui-disabled': {
                                opacity: 0.5,
                              },
                            }}
                          >
                            <span className="text-secondary-black">{t('misMatchedLenses')}</span>
                            <CloseOutlined />
                          </Button>
                        </div>
                      )}
                      {cartItemData.lens?.is_match === 3 && (
                        <div>
                          <Button
                            disabled={batchFlag}
                            className="!text-[12px] !px-[7px] !h-[23px] !text-[#bc1b1b] !w-fit !rounded-[6px]"
                            onClick={handleAddLens}
                            sx={{
                              border: 'none',
                              backgroundColor: '#f8e8e8',
                              '&.Mui-disabled': {
                                opacity: 0.5,
                              },
                            }}
                          >
                            <InfoOutlined />
                            <span className="mx-[4px]">{t('prescriptionErrors')}</span>
                            <CollapseArrowDownOutlined className="text-[15px]" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 价格和数量 */}
                {!lensCanotUse && (
                  <div
                    className={`flex items-center justify-between pt-[10px] ${itemDisabled && itemType === 4 ? 'opacity-50' : ''}`}
                  >
                    {/* 价格 */}
                    <div className={`text-[16px] font-bb`}>
                      {selected && itemPromotion
                        ? formatPrice(
                            Number(itemPromotion.product_payable || 0) +
                              Number(itemPromotion.product_rule_discounts_price || 0),
                          )
                        : formatPrice(getItemTotalPrice())}
                    </div>

                    {/* 数量控制 */}
                    {renderQuantityControl()}
                  </div>
                )}

                {/* 镜片错误时的删除按钮 */}
                {lensCanotUse && (
                  <div className="flex justify-end mt-auto pt-3 pr-3">
                    {batchFlag && (
                      <Button
                        className="w-[25px] h-[25px] flex items-center justify-center !bg-[#f8e8e8] !rounded-[6px] !p-0 !text-[13px] !text-[#bc1b1b] !min-w-[25px]"
                        onClick={() => deleteItem(cartItemData)}
                        sx={{
                          border: 'none',
                          padding: '0px',
                        }}
                      >
                        <DeleteOutlined className="!text-14" />
                      </Button>
                    )}
                  </div>
                )}

                {/* 超库存提示 */}
                {renderOverStockWarning()}

                {/* 赠品倒计时 */}
                {itemType === 4 && (
                  <div className="mt-2">
                    {/* 预售倒计时 */}
                    {giftAllowType === 2 && showPreCountDown && giftCountDown > 0 && (
                      <div className="px-[7px] flex justify-start items-center rounded-[6px] min-h-[20px] box-border bg-gradient-to-r from-[#e7e2ff] to-white text-[10px]">
                        <AnniCountDown
                          key={cartItemData.cart_id}
                          time={giftCountDown}
                          preOrAfter="pre"
                          intro={getIntroText()}
                          onCountDownEnd={handleCountDownEnd}
                        />
                      </div>
                    )}
                    {/* 结束倒计时 - 可结算时显示 */}
                    {giftAllowType === 1 && (
                      <div
                        className={`px-[7px] flex justify-start items-center rounded-[6px] min-h-[20px] box-border bg-gradient-to-r from-[#e7e2ff] to-white text-[10px] ${afterAnniGreaterFlag ? 'hidden' : ''}`}
                      >
                        <AnniCountDown
                          key={cartItemData.cart_id}
                          time={giftCountDown}
                          preOrAfter="after"
                          intro={getIntroText()}
                          onChangeAfterAnniGreaterFlag={handleChangeAfterAnniGreaterFlag}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* 促销相关提示 */}
                {renderPromotionTips()}
              </div>
            </div>
            {/* 赠品组件 */}
            <div className="flex items-center">
              {/* 为了保证赠品组件对齐（修改源代码逻辑太多了） */}
              <div className="pr-[8px]">
                <div className="w-[16px] h-0" />
              </div>
              <GiftsWithCartItem
                className="mt-[12px] pr-[10px] flex justify-end"
                cartItemData={cartItemData}
                promotion={itemPromotion ? [{ id: cartItemData.cart_id, ...itemPromotion }] : []}
              />
            </div>
            {/* 防蓝光镜片升级组件 - 只在有new_lens或已升级时显示 */}
            {((cartItemData.lens as any)?.new_lens || (cartItemData.option as any)?.old_lens_id) &&
              selected && <BlueLightItem cartItemData={cartItemData} onReloadCart={onReloadCart} />}
          </div>
        </SwiperSlide>

        {/* 操作按钮区域 - 仅在非编辑模式下显示 */}
        {!batchFlag && (
          <SwiperSlide className="operation notranslate" style={{ width: 'auto', height: '100%' }}>
            {/* 普通商品操作按钮 */}
            {[1, 4].includes(Number(itemType)) && (
              <div
                className="flex h-full"
                style={IS_DE ? { width: editShow ? 240 : 120 } : { width: editShow ? 165 : 120 }}
              >
                {/* 编辑按钮 */}
                {editShow && (
                  <div
                    className="flex items-center justify-center text-xs font-bs text-white bg-[#ffbd00] cursor-pointer h-[full]"
                    style={{ width: IS_DE ? 80 : 55 }}
                    onClick={() => {
                      handleEditPrescription();
                      closeSwiper();
                    }}
                  >
                    {t('edit')}
                  </div>
                )}

                {/* 心愿单按钮 */}
                <div
                  className="flex items-center justify-center text-xs font-bs text-white bg-[#fe7c44] cursor-pointer"
                  style={IS_DE ? { width: 80 } : { width: editShow ? 55 : 60 }}
                  onClick={() => {
                    handleMoveToWishlist();
                    closeSwiper();
                  }}
                >
                  {t('wishlist')}
                </div>

                {/* 删除按钮 */}
                <div
                  className="flex items-center justify-center text-xs font-bs text-white bg-[#f83a53] cursor-pointer"
                  style={IS_DE ? { width: 80 } : { width: editShow ? 55 : 60 }}
                  onClick={() => {
                    handleDelete();
                  }}
                >
                  {t('delete')}
                </div>
              </div>
            )}

            {/* 兑换品操作按钮 */}
            {itemType === 5 && (
              <div className="flex h-full" style={{ width: editShow ? '149px' : '89px' }}>
                {/* 编辑按钮 */}
                {editShow && (
                  <div
                    className="flex items-center justify-center text-xs font-bs text-white bg-[#fe7c44] cursor-pointer"
                    style={{ width: '60px' }}
                    onClick={() => {
                      handleEditPrescription();
                      closeSwiper();
                    }}
                  >
                    {t('edit')}
                  </div>
                )}

                {/* 取消兑换按钮 */}
                <div
                  className="flex items-center justify-center text-xs font-bs text-white bg-[#463ae8] cursor-pointer"
                  style={{ width: '89px' }}
                  onClick={() => {
                    showCancelRedemptionDialog();
                    closeSwiper();
                  }}
                >
                  <div
                    className="text-center"
                    dangerouslySetInnerHTML={{ __html: t('cancelRedemptionMultiline') }}
                  />
                </div>
              </div>
            )}
          </SwiperSlide>
        )}
      </Swiper>
    </div>
  );
};
